// @flow

import * as React from 'react';
import {useState} from 'react';
import classNames from 'classnames';
import Box from 'sail/Box';
import ModalLayer from 'sail/ModalLayer';
import './Image.scss';

type ImageProps = {|
  src: string,
  id?: string,
  className?: string,
  alt?: string,
  zoomWidth?: string,
  zoomHeight?: string,
  zoomable?: boolean,
  border?: boolean,
  ariaHidden?: boolean,
  width?: string,
  height?: string,
  children?: React.Node,
  aside?: boolean,
  inline?: boolean,
|};

const getImageDimensionProps = (width: ?string, height: ?string) => {
  // If caller has supplied both, just return but convert width to
  // a percentage
  if (width && height) {
    return {
      width: `${width}%`,
      height,
    };
  }
  // If caller has only given a height, return it. Width will be unset.
  if (height) {
    return {
      height,
    };
  }
  // If caller has only given a width, return it as a percentage.
  // height will be unset.
  return {
    // Default to 100% width if caller has supplied nothing.
    width: `${width || 100}%`,
  };
};

const getWrapperClasses = (zoomable: boolean, aside: boolean) => {
  return classNames(
    'Image-wrapper',
    zoomable && 'ZoomableImage-wrapper',
    aside && 'AsideImage-wrapper',
  );
};

export const Image = ({
  src,
  children,
  width,
  height,
  zoomWidth = 1024,
  zoomHeight,
  zoomable = false,
  border = false,
  ariaHidden = false,
  id,
  alt = '',
  className = '',
  aside = false,
  inline = false,
}: ImageProps) => {
  const [isZoomed, setZoomed] = useState(false);
  const imgProps = {
    src,
    alt,
    ...getImageDimensionProps(width, height),
  };

  if (inline) {
    return (
      <img className={classNames('InlineImage', className)} {...imgProps} />
    );
  }

  const image = (
    <img
      className={classNames(
        zoomable && 'ZoomableImage',
        aside && 'AsideImage',
        border && 'BorderImage',
        className,
      )}
      {...imgProps}
    />
  );

  // Aside images shouldn't contribute any vertical padding
  const boxPadding = aside ? {} : {top: 32, bottom: 12};

  if (!zoomable) {
    return (
      <Box
        id={id}
        padding={boxPadding}
        className={getWrapperClasses(zoomable, aside)}
        aria-hidden={ariaHidden}
      >
        {image}
        {children}
      </Box>
    );
  }

  return (
    <Box id={id} padding={boxPadding} aria-hidden={ariaHidden}>
      <a
        onClick={(e) => {
          e.preventDefault();
          setZoomed(true);
        }}
        className={getWrapperClasses(zoomable, aside)}
        role="img"
      >
        {image}
        {children}
      </a>
      <ModalLayer
        shown={isZoomed}
        onWashClick={() => setZoomed(false)}
        onEscape={() => setZoomed(false)}
      >
        <img
          src={src}
          style={{
            maxWidth: zoomWidth,
            maxHeight: zoomHeight,
            objectFit: 'contain',
            margin: '0 auto',
          }}
          className="ZoomableImage-expanded"
          onClick={() => setZoomed(false)}
        />
      </ModalLayer>
    </Box>
  );
};
